import { Fragment, useMemo, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Render del texto del asistente.
 *
 * El modelo responde con un subconjunto de markdown (lo indica su prompt:
 * viñetas y negritas, sin tablas extensas). Se interpreta ese subconjunto y se
 * construye un árbol de React: **no se usa `innerHTML`**, así que el contenido
 * del modelo no puede inyectar HTML.
 *
 * Soporta: párrafos, listas con viñetas, listas numeradas, encabezados,
 * `**negrita**`, `*cursiva*` y `` `código` ``.
 */

const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text
    .split(INLINE_PATTERN)
    .filter((part) => part !== '')
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return (
          <strong key={key} className="font-semibold text-ink-900">
            {part.slice(2, -2)}
          </strong>
        );
      }

      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return (
          <code key={key} className="rounded bg-ink-100 px-1 py-0.5 font-mono text-[0.85em] text-ink-800">
            {part.slice(1, -1)}
          </code>
        );
      }

      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }

      return <Fragment key={key}>{part}</Fragment>;
    });
}

function buildBlocks(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');

  let list: { type: 'ul' | 'ol'; items: string[] } | null = null;

  const flushList = () => {
    if (!list) return;

    const items = list.items;
    const className = 'my-1 space-y-1 pl-5 text-ink-700';

    if (list.type === 'ul') {
      nodes.push(
        <ul key={`ul-${nodes.length}`} className={cn(className, 'list-disc')}>
          {items.map((item, index) => (
            <li key={index}>{renderInline(item, `li-${nodes.length}-${index}`)}</li>
          ))}
        </ul>,
      );
    } else {
      nodes.push(
        <ol key={`ol-${nodes.length}`} className={cn(className, 'list-decimal')}>
          {items.map((item, index) => (
            <li key={index}>{renderInline(item, `li-${nodes.length}-${index}`)}</li>
          ))}
        </ol>,
      );
    }

    list = null;
  };

  lines.forEach((line, lineIndex) => {
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ordered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const heading = /^\s*(#{1,4})\s+(.*)$/.exec(line);

    if (bullet) {
      if (list?.type !== 'ul') {
        flushList();
        list = { type: 'ul', items: [] };
      }
      list.items.push(bullet[1] ?? '');
      return;
    }

    if (ordered) {
      if (list?.type !== 'ol') {
        flushList();
        list = { type: 'ol', items: [] };
      }
      list.items.push(ordered[1] ?? '');
      return;
    }

    flushList();

    const trimmed = line.trim();
    if (trimmed === '') return;

    if (heading) {
      nodes.push(
        <h4 key={`h-${lineIndex}`} className="mt-2 text-[13px] font-semibold text-ink-900">
          {renderInline(heading[2] ?? '', `h-${lineIndex}`)}
        </h4>,
      );
      return;
    }

    nodes.push(
      <p key={`p-${lineIndex}`} className="text-ink-700">
        {renderInline(trimmed, `p-${lineIndex}`)}
      </p>,
    );
  });

  flushList();

  return nodes;
}

interface MarkdownContentProps {
  text: string;
  className?: string;
}

export function MarkdownContent({ text, className }: MarkdownContentProps) {
  const blocks = useMemo(() => buildBlocks(text), [text]);

  return <div className={cn('space-y-1.5 text-sm leading-relaxed', className)}>{blocks}</div>;
}
